import { useMemo } from "react"

import { RefreshCw } from "lucide-react"

import type { StatMetric } from "@/components/shared/StatBand"
import { useSyncStatus } from "@/hooks/useCollections"
import { formatSyncAge, freshnessOf, freshnessTone } from "@/lib/syncFreshness"

/**
 * The "last synced" readout for the monitoring band.
 *
 * Reports the *older* of the two integrations rather than one figure each. Two ages side by side
 * invites the reader to average them, when what matters is the worst one: if Samsara died an hour
 * ago the board is wrong regardless of how recently Track-POD answered. The label names whichever
 * system is behind, so the number stays actionable rather than anonymous.
 *
 * Rendered through StatBand's `metrics` slot.
 */
export function useSyncMetrics(now: Date): StatMetric[] {
  const { data: syncStatus } = useSyncStatus()

  return useMemo(() => {
    const entries = [
      { system: "Samsara", at: syncStatus?.samsara?.started_at ?? null },
      { system: "Track-POD", at: syncStatus?.trackpod?.started_at ?? null },
    ]

    const synced = entries.filter((entry) => entry.at)

    // Nothing has ever run. Before cutover that is the normal state, so the band says nothing
    // rather than spending a slot on an absence.
    if (synced.length === 0) return []

    const oldest = synced.reduce((worst, entry) =>
      new Date(entry.at!).getTime() < new Date(worst.at!).getTime() ? entry : worst
    )

    const freshness = freshnessOf(oldest.at, now)

    return [
      {
        key: "last_synced",
        icon: <RefreshCw size={12} aria-hidden="true" />,
        // Naming the lagging system is what makes the number worth acting on.
        label: freshness === "fresh" ? "Synced" : `${oldest.system} synced`,
        value: formatSyncAge(oldest.at, now),
        tone: freshnessTone(freshness),
      },
    ]
  }, [syncStatus, now])
}
